import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout } from "@/pages/client/ClientHome";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";

export default function Review() {
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/client/review").then((r) => {
    setInfo(r.data);
    if (r.data.review) {
      setRating(r.data.review.rating || 5);
      setText(r.data.review.text || "");
    }
  }).catch(() => setInfo({ error: true }));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/client/review", { rating, text });
      toast.success(t("rv.sent"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!info) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;

  const rev = info.review;
  return (
    <ClientLayout>
      <div className="max-w-xl mx-auto space-y-6" data-testid="client-review-page">
        <div>
          <p className="kc-eyebrow mb-2">{t("rv.leaveTitle")}</p>
          <h1 className="text-3xl font-black uppercase tracking-tight">{t("rv.myTitle")}</h1>
        </div>
        {info.error ? (
          <div className="kc-card p-6" data-testid="review-unavailable">
            <p className="text-sm text-muted-foreground">{t("rv.unavailable")}</p>
          </div>
        ) : !info.eligible && !rev ? (
          <div className="kc-card p-6" data-testid="review-too-early">
            <p className="text-sm text-muted-foreground">{t("rv.tooEarly")}</p>
          </div>
        ) : (
          <>
            {rev && (
              <p data-testid="review-status-badge"
                className={`kc-badge self-start ${rev.status === "published"
                  ? "bg-green-500/10 text-green-700 border border-green-500/30"
                  : "bg-amber-400/10 text-amber-600 border border-amber-400/40"}`}>
                {rev.status === "published" ? t("rv.published") : t("rv.pending")}
              </p>
            )}
            <form onSubmit={submit} className="kc-card p-6 space-y-5" data-testid="client-review-form">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("rv.yourRating")}</label>
                <div className="flex gap-1.5 mt-2" data-testid="client-review-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} data-testid={`client-review-star-${n}`} onClick={() => setRating(n)}>
                      <Star className={`w-8 h-8 transition-colors ${n <= rating ? "fill-primary text-primary" : "text-border"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("rv.text")}</label>
                <textarea data-testid="client-review-text" required rows={4} minLength={10} maxLength={600} value={text}
                  onChange={(e) => setText(e.target.value)} className="kc-input mt-1"
                  placeholder={t("rv.textPlaceholder")} />
              </div>
              <button data-testid="client-review-submit" type="submit" disabled={saving || text.trim().length < 10}
                className="kc-btn-primary inline-flex items-center gap-2">
                <Send className="w-4 h-4" /> {saving ? t("common.loading") : rev ? t("rv.update") : t("rv.submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </ClientLayout>
  );
}
